import { useState, type FormEvent } from 'react'
import { Check } from 'lucide-react'
import Button from '../../components/ui/Button'
import { AccountField, AccountPanel, FIELD_CLASS } from '../../components/account/AccountUi'
import { day } from '../../components/account/status'
import { Loading, Problem } from '../../components/account/AccountStates'
import {
  useAccountQuery,
  useChangePassword,
  useUpdateAccount,
} from '../../hooks/useAccountQueries'
import { useAccount } from '../../hooks/account-context'
import { toApiError } from '../../lib/http'

/* Your details, and your password.

   Two forms rather than one, because they fail differently: contact details
   are a correction and go through on their own, while a password asks you to
   prove you are at the keyboard first. Putting them in one save button would
   mean a wrong current password refusing a change of address.

   The name is not editable here. A customer record is what the showroom
   files people under and what an order is written against — a name changed
   from this page would quietly rewrite the party to something somebody has
   already signed. Ringing the desk is the right way to change one, and the
   page says so rather than showing a field that does not work. */

const PHONE_PATTERN = /^(\+92|0)?[\s-]?3\d{2}[\s-]?\d{7}$/

function Saved({ children }: { children: string }) {
  return (
    <p className="flex items-center gap-2 text-[13px] text-lime">
      <Check aria-hidden="true" className="size-4" strokeWidth={2.2} />
      {children}
    </p>
  )
}

export function AccountOverview() {
  const query = useAccountQuery()
  const { can } = useAccount()

  if (query.isPending) return <Loading label="Loading your details" />
  if (query.isError) return <Problem onRetry={() => void query.refetch()} />

  const customer = query.data?.customer ?? null

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <DetailsForm
        key={customer?.id ?? 'none'}
        customer={customer}
        editable={can('account.edit')}
      />
      <PasswordForm editable={can('account.edit')} />
    </div>
  )
}

/* ---------- contact details ---------- */

function DetailsForm({
  customer,
  editable,
}: {
  customer: { fullName: string; phone: string; email: string; address: string; city: string; since: string } | null
  editable: boolean
}) {
  const update = useUpdateAccount()

  /* Seeded once from the record, and the component is keyed on that record's
     id by its parent — so a refetch that brings back different details
     remounts this rather than overwriting what somebody is halfway through
     typing. */
  const [phone, setPhone] = useState(customer?.phone ?? '')
  const [email, setEmail] = useState(customer?.email ?? '')
  const [address, setAddress] = useState(customer?.address ?? '')
  const [city, setCity] = useState(customer?.city ?? '')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  if (!customer) {
    return (
      <AccountPanel>
        <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-platinum">
          Your details
        </h2>
        <p className="mt-3 text-[13.5px] leading-relaxed text-steel">
          Your sign-in is not attached to a customer file yet. Give the showroom a ring and we
          will link it — everything you have booked with us is already on file.
        </p>
      </AccountPanel>
    )
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setSaved(false)
    setError('')

    if (!PHONE_PATTERN.test(phone.trim())) {
      setFields({ phone: 'Enter a valid mobile number.' })
      return
    }
    setFields({})

    try {
      await update.mutateAsync({ phone, email, address, city })
      setSaved(true)
    } catch (failure) {
      const problem = toApiError(failure)
      setFields(problem.fields ?? {})
      if (!problem.fields) setError(problem.message)
    }
  }

  return (
    <AccountPanel>
      <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-platinum">
        Your details
      </h2>
      <p className="mt-1.5 text-[13px] text-dim">
        {customer.fullName} · with us since {day(customer.since)}
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-5">
        <AccountField label="Mobile number" htmlFor="account-phone" error={fields.phone}>
          <input
            id="account-phone"
            type="tel"
            value={phone}
            disabled={!editable}
            autoComplete="tel"
            onChange={(event) => setPhone(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        <AccountField label="Email" htmlFor="account-email" optional error={fields.email}>
          <input
            id="account-email"
            type="email"
            value={email}
            disabled={!editable}
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        <AccountField label="City" htmlFor="account-city" optional error={fields.city}>
          <input
            id="account-city"
            value={city}
            disabled={!editable}
            autoComplete="address-level2"
            onChange={(event) => setCity(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        <AccountField label="Address" htmlFor="account-address" optional error={fields.address}>
          <input
            id="account-address"
            value={address}
            disabled={!editable}
            autoComplete="street-address"
            onChange={(event) => setAddress(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        {error ? (
          <p role="alert" className="text-[13px] text-red-300">
            {error}
          </p>
        ) : null}
        {saved ? <Saved>Saved.</Saved> : null}

        <p className="text-[12.5px] leading-relaxed text-dim">
          Your name is filed against every order you have placed, so it is changed at the desk
          rather than here. Ring the showroom and we will correct it.
        </p>

        {editable ? (
          <Button type="submit" size="md" disabled={update.isPending} className="self-start">
            {update.isPending ? 'Saving' : 'Save details'}
          </Button>
        ) : null}
      </form>
    </AccountPanel>
  )
}

/* ---------- password ---------- */

function PasswordForm({ editable }: { editable: boolean }) {
  const change = useChangePassword()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setSaved(false)
    setError('')

    if (next.length < 8) {
      setFields({ password: 'Use at least eight characters.' })
      return
    }
    setFields({})

    try {
      await change.mutateAsync({ currentPassword: current, password: next })
      setSaved(true)
      setCurrent('')
      setNext('')
    } catch (failure) {
      const problem = toApiError(failure)
      setFields(problem.fields ?? {})
      if (!problem.fields) setError(problem.message)
    }
  }

  return (
    <AccountPanel>
      <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-platinum">
        Password
      </h2>
      <p className="mt-1.5 text-[13px] text-dim">
        We only ever store a scrambled version, so nobody here can read yours — not even us.
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-5">
        <AccountField
          label="Current password"
          htmlFor="password-current"
          error={fields.currentPassword}
        >
          <input
            id="password-current"
            type="password"
            value={current}
            disabled={!editable}
            autoComplete="current-password"
            onChange={(event) => setCurrent(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        <AccountField
          label="New password"
          htmlFor="password-next"
          error={fields.password}
          hint="Eight characters or more."
        >
          <input
            id="password-next"
            type="password"
            value={next}
            disabled={!editable}
            autoComplete="new-password"
            onChange={(event) => setNext(event.target.value)}
            className={FIELD_CLASS}
          />
        </AccountField>

        {error ? (
          <p role="alert" className="text-[13px] text-red-300">
            {error}
          </p>
        ) : null}
        {saved ? <Saved>Password changed. Your other devices stay signed in.</Saved> : null}

        {editable ? (
          <Button type="submit" size="md" disabled={change.isPending} className="self-start">
            {change.isPending ? 'Changing' : 'Change password'}
          </Button>
        ) : null}
      </form>
    </AccountPanel>
  )
}

export default AccountOverview
